import styled, { css } from 'styled-components'
import { useState, useEffect } from 'react'
import { Formik, Form } from 'formik'
import { SortableContainer, SortableElement } from 'react-sortable-hoc'
import { AttachmentIcon, Button, Grid, Wrapper } from '@farewill/ui'
import { BORDER, COLOR, GTR, FONT } from '@farewill/ui/tokens'
import { DOCUMENT_CATEGORIES } from 'utils/enums'
import Input from 'components/form/input'
import SelectInput from 'components/form/select-input'
import { Uppercase } from 'components/styled/uppercase'
import type { Document } from 'lib/models/document'
import type { PreviewFile, DocumentFormValues } from './types'
import { PdfPreview } from './pdf-preview'
import { DocumentIcon } from 'components/custom-icons'

const StyledDocumentForm = styled(Wrapper)<{ $isPage: boolean }>`
  ${({ $isPage }) =>
    $isPage &&
    css`
      border-color: ${COLOR.GREY.LIGHT};
      border-width: 2px;
      border-style: solid;
      border-radius: ${BORDER.RADIUS.M};
      padding: ${GTR.S};
      background-color: ${COLOR.WHITE};
    `}

  &:not(:last-of-type) {
    margin-bottom: ${GTR.S};
  }
`

const StyledInput = styled(Input)`
  max-height: 59px;
`

const StyledButtonWrapper = styled(Grid)`
  display: flex;
  justify-content: flex-end;
  gap: ${GTR.M};
  align-items: center;
`

const StyledCancelButton = styled(Button)`
  font-weight: ${FONT.WEIGHT.REGULAR};
  text-decoration: underline;
  align-self: center;
  margin-top: ${GTR.XS};
  &:hover {
    text-decoration: underline;
  }
`

const StyledPreviewTileWrapper = styled.span`
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 140px;
  margin-right: ${GTR.M};
  margin-bottom: ${GTR.M};
`

const StyledPreviewImage = styled.img`
  width: 100%;
  max-height: 140px;
  object-fit: contain;
  object-position: bottom left;
  filter: drop-shadow(1px 1px 6px rgba(0, 0, 0, 0.25));
  border-radius: 4px;
`

const StyledFileNameRow = styled.span`
  display: flex;
  align-items: center;
  margin-top: ${GTR.XS};
  width: 100%;
  min-width: 0;
`

const StyledFileNameText = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: ${FONT.SIZE.XS};
`

const StyledNoPreviewTile = styled.span`
  display: flex;
  align-items: center;
  margin-bottom: ${GTR.M};
`

const StyledIcon = styled.div`
  border-radius: 3px;
  border: 2px solid ${COLOR.GREY.LIGHT};
  height: 46px;
  width: 32px;
  margin-right: ${GTR.S};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledAttachmentIcon = styled(AttachmentIcon)`
  width: 16px;
  height: 16px;
`

const validate = () => (values: DocumentFormValues) => {
  const errors: Record<string, string> = {}

  if (!values.title) {
    errors.title = 'Title is required'
  }

  if (!values.category) {
    errors.category = 'Category is required'
  }

  return errors
}

const SortablePreviewTile = SortableElement(
  ({ file, isSortable }: { file: PreviewFile; isSortable: boolean }) => {
    // We could be dragging in a modal, so we must set a suitably high z-index
    const style = isSortable ? { cursor: 'grab', zIndex: 1000 } : {}

    if (file.type?.startsWith('image/')) {
      return (
        <StyledPreviewTileWrapper key={file.name} style={style}>
          <StyledPreviewImage src={file.preview} alt={file.name} />
          <StyledFileNameRow>
            <StyledFileNameText title={file.name}>
              {file.name}
            </StyledFileNameText>
          </StyledFileNameRow>
        </StyledPreviewTileWrapper>
      )
    }

    if (file.type === 'application/pdf') {
      return (
        <StyledPreviewTileWrapper key={file.name} style={style}>
          <PdfPreview
            file={file}
            footer={
              <StyledFileNameRow>
                <StyledFileNameText title={file.name}>
                  {file.name}
                </StyledFileNameText>
              </StyledFileNameRow>
            }
          />
        </StyledPreviewTileWrapper>
      )
    }

    return (
      <StyledNoPreviewTile style={style}>
        <StyledIcon>
          <StyledAttachmentIcon />
        </StyledIcon>
        {file.name}
      </StyledNoPreviewTile>
    )
  }
)

const StyledButtonUnderline = styled(Button.Underline)`
  padding: 0;
  font-weight: ${FONT.WEIGHT.REGULAR};
`

const StyledCompactToggleRow = styled(Wrapper)`
  display: flex;
  align-items: center;
  gap: ${GTR.S};
`

const StyledFileCountIconWrapper = styled(Wrapper)`
  display: flex;
  align-items: center;
  gap: ${GTR.XXS};
`

const SortablePreviewList = SortableContainer(
  ({ files }: { files: PreviewFile[] }) => {
    return (
      <Wrapper margin={['S', 0, 0, 0]} noTrim>
        {files.map((file, index) => (
          <SortablePreviewTile
            key={file.name}
            index={index}
            file={file}
            isSortable={files.length > 1}
          />
        ))}
      </Wrapper>
    )
  }
)

const categoryOptions = (
  isEditMode: boolean,
  category: Document['category']
) =>
  isEditMode
    ? DOCUMENT_CATEGORIES.filter((c) => !c.disabled || c.value === category)
    : DOCUMENT_CATEGORIES.filter((c) => !c.disabled)

type DocumentFormProps = {
  onCancel: () => void
  onSubmit: (values: DocumentFormValues) => void
  isSubmitting: boolean
  title?: string
  category?: Document['category']
  url?: string
  files?: PreviewFile[]
  onFileOrderChanged: (oldIndex: number, newIndex: number) => void
  isEditMode?: boolean
}

export const DocumentForm = ({
  onCancel,
  onSubmit,
  isSubmitting = false,
  title = '',
  category = '',
  url = '',
  files = [],
  onFileOrderChanged,
  isEditMode = false,
}: DocumentFormProps) => {
  const initialValues = { title, category, url }
  const PLACEMENT = isEditMode ? 'MODAL' : 'PAGE'
  const showRearrangeOption = files.length > 1 && PLACEMENT === 'MODAL'
  const [showSortable, setShowSortable] = useState(
    PLACEMENT === 'PAGE' || files.length === 1
  )

  useEffect(() => {
    setShowSortable(PLACEMENT === 'PAGE' || files.length === 1)
  }, [PLACEMENT, files.length])
  const handleSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number
    newIndex: number
  }) => {
    onFileOrderChanged(oldIndex, newIndex)
  }

  return (
    <StyledDocumentForm $isPage={PLACEMENT === 'PAGE'}>
      <Formik
        initialValues={initialValues}
        validate={validate()}
        onSubmit={onSubmit}
      >
        <Form>
          <Wrapper margin={[0, 0, 'M', 0]} noTrim>
            <Grid>
              <Grid.Item span={12} spanFromL={6}>
                <StyledInput label="Title" name="title" />
              </Grid.Item>
              <Grid.Item span={12} spanFromL={6}>
                <SelectInput
                  label="Category"
                  name="category"
                  options={categoryOptions(isEditMode, category)}
                />
              </Grid.Item>
            </Grid>
          </Wrapper>
          {PLACEMENT === 'MODAL' ? (
            <StyledCompactToggleRow>
              <StyledFileCountIconWrapper>
                <DocumentIcon color={COLOR.GREY.DARK} /> {files.length}{' '}
                {files.length === 1 ? 'file' : 'files'}
              </StyledFileCountIconWrapper>
              {showRearrangeOption && (
                <StyledButtonUnderline
                  onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                    e.preventDefault()
                    setShowSortable(!showSortable)
                  }}
                >
                  Rearrange
                </StyledButtonUnderline>
              )}
            </StyledCompactToggleRow>
          ) : (
            <Uppercase>{files.length > 1 ? 'Arrange files' : 'File'}</Uppercase>
          )}
          {showSortable && (
            <SortablePreviewList
              files={files}
              onSortEnd={handleSortEnd}
              axis="xy"
              shouldCancelStart={() => files.length < 2}
            />
          )}
          <StyledButtonWrapper>
            <StyledCancelButton type="button" onClick={onCancel}>
              Cancel
            </StyledCancelButton>
            <Button.Primary type="submit" loading={isSubmitting}>
              {isEditMode ? 'Save Changes' : 'Upload'}
            </Button.Primary>
          </StyledButtonWrapper>
        </Form>
      </Formik>
    </StyledDocumentForm>
  )
}
